import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { UsageFeatureInfo, UsageStats } from '../types/api'

interface BackendMeResponse {
  plan_code: string
  features: UsageFeatureInfo[]
}

// 백엔드 feature 코드 → 프론트 camelCase 키
// safety_education_pptx → safetyEducationPptx
// safety_education_video → safetyEducationVideo
// safetyEducation 은 두 값의 합 (호환성 유지)
const FEATURE_UI_KEY: Record<string, keyof Omit<UsageStats, 'plan_code' | 'features' | 'imageAnalyses' | 'jsaGenerated' | 'chatMessages' | 'safetyEducation'>> = {
  chat: 'chatbot',
  jsa: 'jsa',
  tbm: 'tbm',
  image_analysis: 'imageAnalysis',
  corrective: 'corrective',
  risk_assessment: 'riskAssessment',
  work_permit: 'workPermit',
  accident_report: 'accidentReport',
  safety_education_pptx: 'safetyEducationPptx',
  safety_education_video: 'safetyEducationVideo',
  eta: 'eta',
  fta: 'fta',
  safety_cost: 'safetyCost',
}

function mapToUsageStats(resp: BackendMeResponse): UsageStats {
  const counts = {
    chatbot: 0, jsa: 0, tbm: 0, imageAnalysis: 0,
    corrective: 0, riskAssessment: 0,
    workPermit: 0, accidentReport: 0,
    safetyEducationPptx: 0, safetyEducationVideo: 0,
    eta: 0, fta: 0, safetyCost: 0,
  }
  for (const f of resp.features ?? []) {
    const uiKey = FEATURE_UI_KEY[f.feature]
    if (uiKey) counts[uiKey] = f.count ?? 0
  }
  return {
    plan_code: resp.plan_code,
    features: resp.features ?? [],
    ...counts,
    safetyEducation: counts.safetyEducationPptx + counts.safetyEducationVideo,
    // Dashboard.tsx 호환 alias
    imageAnalyses: counts.imageAnalysis,
    jsaGenerated: counts.jsa,
    chatMessages: counts.chatbot,
  }
}

export function useUsage() {
  return useQuery({
    queryKey: ['usage', 'current'],
    queryFn: async () => {
      const { data } = await api.get<BackendMeResponse>('/usage/me')
      return mapToUsageStats(data)
    },
    refetchInterval: 1000 * 60 * 5,
  })
}
